// Edit Link — full-screen panel with fixed footer, UTM editor, password, redirect type
import React, { useState, useEffect, useCallback } from "react";
import { FiX, FiArrowLeft, FiExternalLink, FiEdit2, FiChevronDown, FiChevronUp, FiRotateCcw } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "./ToastProvider";
import {
  localInputToUTC,
  utcToLocalInput,
  formatDateTimeWithTz,
  getUserTimezone,
  getTimezoneAbbreviation,
} from "../utils/timezone";
import "../styles/Modal.css";

const BASE_DOMAIN = (process.env.REACT_APP_BASE_URL || "https://linksquire.com").replace(/\/+$/, "");

// Parse UTM params from a URL string
function parseUtmFromUrl(urlStr) {
  const utm = { source: "", medium: "", campaign: "", term: "", content: "" };
  let baseUrl = urlStr;
  try {
    const u = new URL(urlStr);
    utm.source = u.searchParams.get("utm_source") || "";
    utm.medium = u.searchParams.get("utm_medium") || "";
    utm.campaign = u.searchParams.get("utm_campaign") || "";
    utm.term = u.searchParams.get("utm_term") || "";
    utm.content = u.searchParams.get("utm_content") || "";
    u.searchParams.delete("utm_source");
    u.searchParams.delete("utm_medium");
    u.searchParams.delete("utm_campaign");
    u.searchParams.delete("utm_term");
    u.searchParams.delete("utm_content");
    baseUrl = u.toString();
  } catch {}
  return { baseUrl, utm };
}

// Strip protocol from URL for editing (user edits path only)
function stripProtocol(url) {
  return url.replace(/^https?:\/\//, "");
}

// Validate URL format (http/https)
function isValidUrl(url) {
  if (!url) return true; // empty is valid (not required)
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function getShortLinkDomain(link, fallbackDomain) {
  if (link?.domain) return link.domain;
  try {
    return new URL(link?.short_url || "").hostname || fallbackDomain;
  } catch {
    return fallbackDomain;
  }
}

export default function EditLinkModal({ link, onClose, onUpdated }) {
  const parsed = parseUtmFromUrl(link.original_url);
  
  // Convert UTC expiry to local time for input display
  const expiryLocal = utcToLocalInput(link.expires_at);

  // Initial values (for dirty checking and reset)
  const initValues = {
    destPath: stripProtocol(parsed.baseUrl),
    title: link.title || "",
    campaignId: link.campaign_id || "",
    shortCode: link.short_code,
    tags: link.tags || "",
    mobileUrl: link.mobile_url || "",
    redirectTime: link.redirect_time != null ? link.redirect_time : 10,
    redirectType: link.redirect_type || "302",
    utm: { ...parsed.utm },
    expiryDate: expiryLocal.date,
    expiryTime: expiryLocal.time,
    scheduledAt: link.scheduled_at || "",
    useCustomOg: !!(link.og_title || link.og_description || link.og_image),
    ogTitle: link.og_title || "",
    ogDescription: link.og_description || "",
    ogImage: link.og_image || "",
    fallbackMode: link.fallback_mode || "",
    fallbackUrl: link.fallback_url || "",
    fallbackMessage: link.fallback_message || "",
    fallbackCtaText: link.fallback_cta_text || "",
    fallbackCtaUrl: link.fallback_cta_url || "",
    fallbackAutoRedirect: link.fallback_auto_redirect || false,
    fallbackDelay: link.fallback_delay != null ? link.fallback_delay : 5,
    customBrandIcon: link.custom_brand_icon || "",
    customBrandName: link.custom_brand_name || "",
  };

  // Re-initialize values when link prop changes (e.g., after save)
  useEffect(() => {
    const newParsed = parseUtmFromUrl(link.original_url);
    const newExpiryLocal = utcToLocalInput(link.expires_at);
    const newScheduledLocal = link.scheduled_at ? utcToLocalInput(link.scheduled_at) : { date: "", time: "" };
    const newInitValues = {
      destPath: stripProtocol(newParsed.baseUrl),
      title: link.title || "",
      shortCode: link.short_code,
      tags: link.tags || "",
      mobileUrl: link.mobile_url || "",
      redirectTime: link.redirect_time != null ? link.redirect_time : 10,
      redirectType: link.redirect_type || "302",
      utm: { ...newParsed.utm },
      expiryDate: newExpiryLocal.date,
      expiryTime: newExpiryLocal.time,
      scheduledAt: link.scheduled_at || "",
      useCustomOg: !!(link.og_title || link.og_description || link.og_image),
      ogTitle: link.og_title || "",
      ogDescription: link.og_description || "",
      ogImage: link.og_image || "",
      fallbackMode: link.fallback_mode || "",
      fallbackUrl: link.fallback_url || "",
      fallbackMessage: link.fallback_message || "",
      fallbackCtaText: link.fallback_cta_text || "",
      fallbackCtaUrl: link.fallback_cta_url || "",
      fallbackAutoRedirect: link.fallback_auto_redirect || false,
      fallbackDelay: link.fallback_delay != null ? link.fallback_delay : 5,
      customBrandIcon: link.custom_brand_icon || "",
      customBrandName: link.custom_brand_name || "",
    };

    setDestPath(newInitValues.destPath);
    setTitle(newInitValues.title);
    setCampaignId(newInitValues.campaignId);
    setShortCode(newInitValues.shortCode);
    setTags(newInitValues.tags);
    setMobileUrl(newInitValues.mobileUrl);
    setRedirectTime(newInitValues.redirectTime);
    setRedirectType(newInitValues.redirectType);
    setUtm({ ...newInitValues.utm });
    setExpiryDate(newInitValues.expiryDate);
    setExpiryTime(newInitValues.expiryTime);
    setScheduleDate(newScheduledLocal.date);
    setScheduleTime(newScheduledLocal.time || "00:00");
    setUseCustomOg(newInitValues.useCustomOg);
    setOgTitle(newInitValues.ogTitle);
    setOgDescription(newInitValues.ogDescription);
    setOgImage(newInitValues.ogImage);
    setFallbackMode(newInitValues.fallbackMode);
    setFallbackUrl(newInitValues.fallbackUrl);
    setFallbackMessage(newInitValues.fallbackMessage);
    setFallbackCtaText(newInitValues.fallbackCtaText);
    setFallbackCtaUrl(newInitValues.fallbackCtaUrl);
    setFallbackAutoRedirect(newInitValues.fallbackAutoRedirect);
    setFallbackDelay(newInitValues.fallbackDelay);
    setCustomBrandIcon(newInitValues.customBrandIcon || "");
    setCustomBrandName(newInitValues.customBrandName || "");
  }, [link]);

  const [destPath, setDestPath] = useState(initValues.destPath);
  const [title, setTitle] = useState(initValues.title);
  const [campaignId, setCampaignId] = useState(initValues.campaignId);
  const [campaigns, setCampaigns] = useState([]);
  const [shortCode, setShortCode] = useState(initValues.shortCode);
  const [tags, setTags] = useState(initValues.tags);
  const [mobileUrl, setMobileUrl] = useState(initValues.mobileUrl);
  const [redirectTime, setRedirectTime] = useState(initValues.redirectTime);
  const [redirectType, setRedirectType] = useState(initValues.redirectType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { addToast } = useToast();

  // Base domain options
  const defaultDomain = (process.env.REACT_APP_BASE_URL || "https://sqir.link").replace(/^https?:\/\//, '').replace(/\/+$/, "");
  const currentDomain = getShortLinkDomain(link, defaultDomain);
  const [availableDomains, setAvailableDomains] = useState([defaultDomain]);
  const [selectedDomain, setSelectedDomain] = useState(currentDomain);

  // Fetch domains on mount
  useEffect(() => {
    async function fetchCampaigns() {
      try {
        const res = await api.getCampaigns();
        setCampaigns(res.campaigns || []);
      } catch (err) { console.error(err); }
    }
    fetchCampaigns();

    async function fetchDomains() {
      try {
        const data = await api.getDomains();
        const domainsList = data.domains || data || [];
        if (Array.isArray(domainsList)) {
          const verifiedDomains = domainsList
            .filter(d => d.status === 'active')
            .map(d => d.domain);
          if (verifiedDomains.length > 0) {
            setAvailableDomains([defaultDomain, ...verifiedDomains]);
          }
        }
      } catch (err) {
        console.error("Failed to load domains", err);
      }
    }
    fetchDomains();
  }, [defaultDomain]);

  useEffect(() => {
    setSelectedDomain(currentDomain);
  }, [currentDomain]);

  useEffect(() => {
    if (currentDomain && !availableDomains.includes(currentDomain)) {
      setAvailableDomains((prev) => [currentDomain, ...prev.filter((domain) => domain !== currentDomain)]);
    }
  }, [currentDomain, availableDomains]);

  // Inline edit toggles
  const [editingUrl, setEditingUrl] = useState(false);
  const [editingCode, setEditingCode] = useState(false);

  // UTM state — collapsed by default (even if UTM exists)
  const [showUtm, setShowUtm] = useState(false);
  const [utm, setUtm] = useState({ ...initValues.utm });

  // Advanced - collapsed by default
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Password state
  const [password, setPassword] = useState("");
  const [changePassword, setChangePassword] = useState(false);

  // Confirmation dialog state
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const [expiryDate, setExpiryDate] = useState(initValues.expiryDate);
  const [expiryTime, setExpiryTime] = useState(initValues.expiryTime);

  // Scheduling state
  const scheduledInit = initValues.scheduledAt ? utcToLocalInput(initValues.scheduledAt) : { date: "", time: "" };
  const [scheduleDate, setScheduleDate] = useState(scheduledInit.date);
  const [scheduleTime, setScheduleTime] = useState(scheduledInit.time || "00:00");

  // Custom OG tags state
  const [useCustomOg, setUseCustomOg] = useState(initValues.useCustomOg);
  const [ogTitle, setOgTitle] = useState(initValues.ogTitle);
  const [ogDescription, setOgDescription] = useState(initValues.ogDescription);
  const [ogImage, setOgImage] = useState(initValues.ogImage);

  // Expiration fallback state
  const [showFallback, setShowFallback] = useState(!!initValues.fallbackMode);
  const [fallbackMode, setFallbackMode] = useState(initValues.fallbackMode);
  const [fallbackUrl, setFallbackUrl] = useState(initValues.fallbackUrl);
  const [fallbackMessage, setFallbackMessage] = useState(initValues.fallbackMessage);
  const [fallbackCtaText, setFallbackCtaText] = useState(initValues.fallbackCtaText);
  const [fallbackCtaUrl, setFallbackCtaUrl] = useState(initValues.fallbackCtaUrl);
  const [fallbackAutoRedirect, setFallbackAutoRedirect] = useState(initValues.fallbackAutoRedirect);
  const [fallbackDelay, setFallbackDelay] = useState(initValues.fallbackDelay);
  const [customBrandIcon, setCustomBrandIcon] = useState(initValues.customBrandIcon || "");
  const [customBrandName, setCustomBrandName] = useState(initValues.customBrandName || "");

  // Validation errors for fallback fields
  const fallbackUrlValid = fallbackMode !== "redirect" || isValidUrl(fallbackUrl);
  const fallbackCtaUrlValid = fallbackMode !== "page" || isValidUrl(fallbackCtaUrl);
  const fallbackDelayValid = fallbackDelay >= 1 && fallbackDelay <= 60;
  const fallbackFieldsValid = fallbackUrlValid && fallbackCtaUrlValid && fallbackDelayValid;

  // Toggle warning dialog state
  const [showToggleWarning, setShowToggleWarning] = useState(null); // 'instant' or 'custom_og' or null

  const handleToggleInstantRedirect = (checked) => {
    if (checked && useCustomOg) {
      setShowToggleWarning('instant');
    } else {
      setRedirectTime(checked ? 0 : 10);
    }
  };

  const handleToggleCustomOg = (checked) => {
    if (checked && redirectTime === 0) {
      setShowToggleWarning('custom_og');
    } else {
      setUseCustomOg(checked);
    }
  };

  const confirmToggle = () => {
    if (showToggleWarning === 'instant') {
      setUseCustomOg(false);
      setOgTitle("");
      setOgDescription("");
      setOgImage("");
      setRedirectTime(0);
    } else if (showToggleWarning === 'custom_og') {
      setRedirectTime(10);
      setUseCustomOg(true);
    }
    setShowToggleWarning(null);
  };

  // Check if any field has been modified
  const hasChanges = useCallback(() => {
    // Re-calculate init values on the fly to avoid stale closure issues
    const currentParsed = parseUtmFromUrl(link.original_url);
    const currentExpiryLocal = utcToLocalInput(link.expires_at);
    const currentInit = {
      destPath: stripProtocol(currentParsed.baseUrl),
      title: link.title || "",
      shortCode: link.short_code,
      tags: link.tags || "",
      mobileUrl: link.mobile_url || "",
      redirectTime: link.redirect_time != null ? link.redirect_time : 10,
      redirectType: link.redirect_type || "302",
      utm: { ...currentParsed.utm },
      expiryDate: currentExpiryLocal.date,
      expiryTime: currentExpiryLocal.time,
      scheduledAt: link.scheduled_at || "",
      useCustomOg: !!(link.og_title || link.og_description || link.og_image),
      ogTitle: link.og_title || "",
      ogDescription: link.og_description || "",
      ogImage: link.og_image || "",
      fallbackMode: link.fallback_mode || "",
      fallbackUrl: link.fallback_url || "",
      fallbackMessage: link.fallback_message || "",
      fallbackCtaText: link.fallback_cta_text || "",
      fallbackCtaUrl: link.fallback_cta_url || "",
      fallbackAutoRedirect: link.fallback_auto_redirect || false,
      fallbackDelay: link.fallback_delay != null ? link.fallback_delay : 5,
    };

    return (
      destPath !== currentInit.destPath ||
      title !== currentInit.title ||
      shortCode !== currentInit.shortCode ||
      selectedDomain !== currentDomain ||
      tags !== currentInit.tags ||
      mobileUrl !== currentInit.mobileUrl ||
      redirectTime !== currentInit.redirectTime ||
      redirectType !== currentInit.redirectType ||
      expiryDate !== currentInit.expiryDate ||
      expiryTime !== currentInit.expiryTime ||
      scheduleDate !== currentInit.scheduledAt.split("T")[0] ||
      scheduleTime !== (currentInit.scheduledAt ? currentInit.scheduledAt.split("T")[1]?.substring(0, 5) || "00:00" : "") ||
      utm.source !== currentInit.utm.source ||
      utm.medium !== currentInit.utm.medium ||
      utm.campaign !== currentInit.utm.campaign ||
      utm.term !== currentInit.utm.term ||
      utm.content !== currentInit.utm.content ||
      useCustomOg !== currentInit.useCustomOg ||
      ogTitle !== currentInit.ogTitle ||
      ogDescription !== currentInit.ogDescription ||
      ogImage !== currentInit.ogImage ||
      fallbackMode !== currentInit.fallbackMode ||
      fallbackUrl !== currentInit.fallbackUrl ||
      fallbackMessage !== currentInit.fallbackMessage ||
      fallbackCtaText !== currentInit.fallbackCtaText ||
      fallbackCtaUrl !== currentInit.fallbackCtaUrl ||
      fallbackAutoRedirect !== currentInit.fallbackAutoRedirect ||
      fallbackDelay !== currentInit.fallbackDelay ||
      customBrandIcon !== (currentInit.customBrandIcon || "") ||
      customBrandName !== (currentInit.customBrandName || "") ||
      changePassword
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destPath, title, shortCode, selectedDomain, tags, mobileUrl, redirectTime, redirectType, expiryDate, expiryTime, utm, useCustomOg, ogTitle, ogDescription, ogImage, fallbackMode, fallbackUrl, fallbackMessage, fallbackCtaText, fallbackCtaUrl, fallbackAutoRedirect, fallbackDelay, changePassword, link, currentDomain]);

  const resetAll = () => {
    const currentParsed = parseUtmFromUrl(link.original_url);
    const currentExpiryLocal = utcToLocalInput(link.expires_at);
    const currentInit = {
      destPath: stripProtocol(currentParsed.baseUrl),
      title: link.title || "",
      shortCode: link.short_code,
      tags: link.tags || "",
      mobileUrl: link.mobile_url || "",
      redirectTime: link.redirect_time != null ? link.redirect_time : 10,
      redirectType: link.redirect_type || "302",
      utm: { ...currentParsed.utm },
      expiryDate: currentExpiryLocal.date,
      expiryTime: currentExpiryLocal.time,
      scheduledAt: link.scheduled_at || "",
      useCustomOg: !!(link.og_title || link.og_description || link.og_image),
      ogTitle: link.og_title || "",
      ogDescription: link.og_description || "",
      ogImage: link.og_image || "",
      fallbackMode: link.fallback_mode || "",
      fallbackUrl: link.fallback_url || "",
      fallbackMessage: link.fallback_message || "",
      fallbackCtaText: link.fallback_cta_text || "",
      fallbackCtaUrl: link.fallback_cta_url || "",
      fallbackAutoRedirect: link.fallback_auto_redirect || false,
      fallbackDelay: link.fallback_delay != null ? link.fallback_delay : 5,
    };

    setDestPath(currentInit.destPath);
    setTitle(currentInit.title);
    setShortCode(currentInit.shortCode);
    setTags(currentInit.tags);
    setMobileUrl(currentInit.mobileUrl);
    setRedirectTime(currentInit.redirectTime);
    setRedirectType(currentInit.redirectType);
    setExpiryDate(currentInit.expiryDate);
    setExpiryTime(currentInit.expiryTime);
    const schedLocal = currentInit.scheduledAt ? utcToLocalInput(currentInit.scheduledAt) : { date: "", time: "" };
    setScheduleDate(schedLocal.date);
    setScheduleTime(schedLocal.time || "00:00");
    setUtm({ ...currentInit.utm });
    setUseCustomOg(currentInit.useCustomOg);
    setOgTitle(currentInit.ogTitle);
    setOgDescription(currentInit.ogDescription);
    setOgImage(currentInit.ogImage);
    setFallbackMode(currentInit.fallbackMode);
    setFallbackUrl(currentInit.fallbackUrl);
    setFallbackMessage(currentInit.fallbackMessage);
    setFallbackCtaText(currentInit.fallbackCtaText);
    setFallbackCtaUrl(currentInit.fallbackCtaUrl);
    setFallbackAutoRedirect(currentInit.fallbackAutoRedirect);
    setFallbackDelay(currentInit.fallbackDelay);
    setCustomBrandIcon(currentInit.customBrandIcon || "");
    setCustomBrandName(currentInit.customBrandName || "");
    setShowFallback(!!currentInit.fallbackMode);
    setPassword("");
    setChangePassword(false);
    setEditingUrl(false);
    setEditingCode(false);
    setError("");
  };

  const handleBack = () => {
    if (hasChanges()) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  };

  const handleReset = () => {
    if (hasChanges()) {
      if (window.confirm("Reset all changes? This will revert everything to the last saved state.")) {
        resetAll();
        addToast("Changes reset", "success");
      }
    }
  };

  const fullBaseUrl = `https://${destPath}`;

  const buildUrlWithUtm = () => {
    if (!destPath) return "";
    try {
      const u = new URL(fullBaseUrl);
      if (utm.source) u.searchParams.set("utm_source", utm.source);
      if (utm.medium) u.searchParams.set("utm_medium", utm.medium);
      if (utm.campaign) u.searchParams.set("utm_campaign", utm.campaign);
      if (utm.term) u.searchParams.set("utm_term", utm.term);
      if (utm.content) u.searchParams.set("utm_content", utm.content);
      return u.toString();
    } catch {
      return fullBaseUrl;
    }
  };

  const hasUtm = Object.values(utm).some(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const finalUrl = buildUrlWithUtm();
      const body = {
        original_url: finalUrl,
        title,
        campaign_id: campaignId || null,
        tags,
        mobile_url: mobileUrl,
        redirect_time: redirectTime,
        redirect_type: redirectType,
        expires_at: expiryDate ? localInputToUTC(expiryDate, expiryTime || "12:00") : null,
        scheduled_at: scheduleDate ? localInputToUTC(scheduleDate, scheduleTime || "00:00") : null,
        timezone: getUserTimezone(),
        // Fallback fields
        fallback_mode: fallbackMode || "",
        fallback_url: fallbackMode === "redirect" ? fallbackUrl : "",
        fallback_message: fallbackMode === "page" ? fallbackMessage : "",
        fallback_cta_text: fallbackMode === "page" ? fallbackCtaText : "",
        fallback_cta_url: fallbackMode === "page" ? fallbackCtaUrl : "",
        fallback_auto_redirect: fallbackMode === "page" ? fallbackAutoRedirect : false,
        fallback_delay: fallbackMode === "page" ? fallbackDelay : 5,
        // Custom branding fields (only for page mode)
        custom_brand_icon: fallbackMode === "page" ? customBrandIcon.trim() : "",
        custom_brand_name: fallbackMode === "page" ? customBrandName.trim() : "",
      };

      if (useCustomOg) {
        body.og_title = ogTitle.trim();
        body.og_description = ogDescription.trim();
        body.og_image = ogImage.trim();
      } else {
        body.og_title = "";
        body.og_description = "";
        body.og_image = "";
      }

      if (shortCode !== link.short_code) {
        body.short_code = shortCode;
      }
      if (selectedDomain !== currentDomain) {
        body.domain = selectedDomain === defaultDomain ? "" : selectedDomain;
      }
      if (changePassword) {
        body.password = password;
      }
      const result = await api.updateLink(link.id, body);
      addToast("Link updated successfully!", "success");
      if (onUpdated) onUpdated(result.link);
    } catch (err) {
      setError(err.message);
      addToast("Failed to update link", "error");
    } finally {
      setLoading(false);
    }
  };

  const shortUrl = `${selectedDomain ? `https://${selectedDomain}` : BASE_DOMAIN}/${shortCode}`;
  const displayUrl = buildUrlWithUtm() || fullBaseUrl;

  return (
    <div className="edit-fullscreen">
      <div className="edit-topbar">
        <button className="edit-back-btn" onClick={handleBack}>
          <FiArrowLeft size={18} />
          <span>Back</span>
        </button>
        <h2>Edit Link</h2>
        <div className="edit-topbar-spacer" />
      </div>

      <div className="edit-body">
        <div className="edit-panel">
          <form onSubmit={handleSubmit} className="modal-form edit-form" id="edit-form">
            {error && <div className="form-error">{error}</div>}

            {/* Details — at the top */}
            <div className="edit-form-section edit-form-section-no-top-border">
              <h3 className="edit-section-title">Details</h3>
              <div className="form-group">
                <label htmlFor="edit-title">Title</label>
                <input id="edit-title" type="text" value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your link a name (won't affect the link)" maxLength={255} />
              </div>
              <div className="form-group">
                <label htmlFor="campaignId">Campaign</label>
                <select
                  id="campaignId"
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                >
                  <option value="">-- No Campaign --</option>
                  {campaigns.map(c => (
                    <option key={c.$id} value={c.$id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="edit-tags">Tags (comma-separated)</label>
                <input id="edit-tags" type="text" value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="eg., marketing, social, campaign" />
              </div>
            </div>

            {/* Link display section */}
            <div className="edit-form-section">
              <div className="edit-link-display">
                <div className="edit-link-row">
                  <span className="edit-link-label">Short Link</span>
                  {editingCode ? (
                    <div 
                      className="code-input-group" 
                      style={{ flex: 1, display: 'flex', gap: '8px', background: 'transparent', border: 'none', padding: 0 }}
                      onBlur={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                          setEditingCode(false);
                        }
                      }}
                    >
                      <select 
                        value={selectedDomain} 
                        onChange={(e) => setSelectedDomain(e.target.value)}
                        style={{ 
                          padding: '8px 30px 8px 12px', 
                          border: '1px solid var(--border)', 
                          borderRadius: 'var(--radius)', 
                          background: 'var(--surface)',
                          fontSize: '14px',
                          color: 'var(--text)'
                        }}
                      >
                        {availableDomains.map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <div style={{ flex: 1, position: 'relative', display: 'flex' }}>
                        <span className="code-prefix" style={{ borderRight: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>/</span>
                        <input
                          type="text"
                          value={shortCode}
                          onChange={(e) => setShortCode(e.target.value)}
                          autoFocus
                          maxLength={100}
                          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, flex: 1 }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="edit-link-value-row">
                      <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="edit-link-value">
                        {shortUrl} <FiExternalLink size={12} />
                      </a>
                      <button type="button" className="edit-inline-btn" onClick={() => setEditingCode(true)}>
                        <FiEdit2 size={13} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="edit-link-row">
                  <span className="edit-link-label">Destination</span>
                  {editingUrl ? (
                    <div className="code-input-group" style={{ flex: 1 }}>
                      <span className="code-prefix">https://</span>
                      <input
                        type="text"
                        value={destPath}
                        onChange={(e) => setDestPath(e.target.value)}
                        autoFocus
                        onBlur={() => setEditingUrl(false)}
                        required
                      />
                    </div>
                  ) : (
                    <div className="edit-link-value-row">
                      <a href={displayUrl} target="_blank" rel="noopener noreferrer" className="edit-link-value edit-link-dest">
                        {displayUrl} <FiExternalLink size={12} />
                      </a>
                      <button type="button" className="edit-inline-btn" onClick={() => setEditingUrl(true)}>
                        <FiEdit2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Instant redirect toggle */}
            <div className="edit-form-section">
              <div className="toggle-row">
                <div className="toggle-label-group">
                  <span className="toggle-label">Instant redirect</span>
                  <span className="toggle-help">
                    <span className="toggle-help-icon">?</span>
                    <span className="toggle-tooltip">
                      Skip the preview landing page and redirect visitors instantly. When off, visitors see a branded preview page with a countdown timer before being redirected.{" "}
                      <a href={`${BASE_DOMAIN}/preview-demo`} target="_blank" rel="noopener noreferrer">See example preview page</a>
                    </span>
                  </span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={redirectTime === 0} onChange={(e) => handleToggleInstantRedirect(e.target.checked)} />
                  <span className="toggle-switch-slider" />
                </label>
              </div>
            </div>

            <div className="edit-form-section">
              <div className="toggle-row">
                <div className="toggle-label-group">
                  <span className="toggle-label">Customize Social Preview</span>
                  <span className="toggle-help">
                    <span className="toggle-help-icon">?</span>
                    <span className="toggle-tooltip">
                      Override the default Open Graph image, title, and description when sharing this link on social media. (Note: Custom previews force a short loading page to prevent abuse).
                    </span>
                  </span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={useCustomOg} onChange={(e) => handleToggleCustomOg(e.target.checked)} />
                  <span className="toggle-switch-slider" />
                </label>
              </div>
            </div>

            {useCustomOg && (
              <div className="edit-form-section" style={{ borderTop: "none", paddingTop: 0 }}>
                <div className="custom-og-builder">
                  <div className="form-group">
                    <label>Title</label>
                    <input type="text" value={ogTitle} onChange={(e) => setOgTitle(e.target.value)} placeholder="Custom preview title" />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea value={ogDescription} onChange={(e) => setOgDescription(e.target.value)} placeholder="Custom preview description" rows={2} style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }} />
                  </div>
                  <div className="form-group">
                    <label>Image URL</label>
                    <input type="url" value={ogImage} onChange={(e) => setOgImage(e.target.value)} placeholder="https://example.com/image.png" />
                  </div>
                  {/* Custom OG Preview Card */}
                  {(ogTitle || ogImage) && (
                    <div className="og-preview-card" style={{ marginTop: '16px' }}>
                      {ogImage && (
                        <img src={ogImage} alt="" className="og-preview-img" onError={(e) => e.target.style.display = 'none'} />
                      )}
                      <div className="og-preview-text">
                        <span className="og-site-name">LinkSquire Redirect</span>
                        <span className="og-title">{ogTitle || "Custom Title"}</span>
                        {ogDescription && (
                          <span className="og-desc">{ogDescription.substring(0, 120)}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* UTM Builder — collapsed by default */}
            <div className="form-section-toggle" onClick={() => setShowUtm(!showUtm)}>
              <span>UTM Builder {hasUtm && <span className="section-badge">Active</span>}</span>
              {showUtm ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </div>
            {showUtm && (
              <div className="utm-builder">
                <div className="form-row">
                  <div className="form-group">
                    <label>Source</label>
                    <input type="text" value={utm.source} onChange={(e) => setUtm({ ...utm, source: e.target.value })} placeholder="eg., google" />
                  </div>
                  <div className="form-group">
                    <label>Medium</label>
                    <input type="text" value={utm.medium} onChange={(e) => setUtm({ ...utm, medium: e.target.value })} placeholder="eg., cpc" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Campaign</label>
                  <input type="text" value={utm.campaign} onChange={(e) => setUtm({ ...utm, campaign: e.target.value })} placeholder="eg., spring_sale" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Term</label>
                    <input type="text" value={utm.term} onChange={(e) => setUtm({ ...utm, term: e.target.value })} placeholder="eg., keyword" />
                  </div>
                  <div className="form-group">
                    <label>Content</label>
                    <input type="text" value={utm.content} onChange={(e) => setUtm({ ...utm, content: e.target.value })} placeholder="eg., banner_ad" />
                  </div>
                </div>
                {hasUtm && (
                  <div className="utm-preview">
                    <label>Preview URL</label>
                    <code>{buildUrlWithUtm()}</code>
                  </div>
                )}
              </div>
            )}

            {/* Advanced — collapsed by default */}
            <div className="form-section-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
              <span>Advanced Options</span>
              {showAdvanced ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </div>
            {showAdvanced && (
              <div className="advanced-options">
                <div className="form-group">
                  <label htmlFor="edit-mobile">Mobile URL (optional)</label>
                  <input id="edit-mobile" type="url" value={mobileUrl}
                    onChange={(e) => setMobileUrl(e.target.value)}
                    placeholder="https://m.example.com/page" />
                  <span className="form-hint">Mobile users will be redirected here instead</span>
                </div>

                <div className="form-group">
                  <label>Expires at <span className="tz-indicator">({getTimezoneAbbreviation()})</span></label>
                  <div className="datetime-picker">
                    <input type="date" value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)} className="date-input" />
                    <input type="time" value={expiryTime}
                      onChange={(e) => setExpiryTime(e.target.value)}
                      disabled={!expiryDate}
                      className={`time-input${!expiryDate ? " input-disabled" : ""}`} />
                    {expiryDate && (
                      <button type="button" className="clear-date-btn"
                        onClick={() => { setExpiryDate(""); setExpiryTime("12:00"); }}>
                        <FiX size={12} />
                      </button>
                    )}
                  </div>
                  {expiryDate && (
                    <span className="form-hint" style={{ textAlign: "center" }}>
                      Expires: {formatDateTimeWithTz(localInputToUTC(expiryDate, expiryTime))}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>Schedule activation <span className="tz-indicator">({getTimezoneAbbreviation()})</span></label>
                  <div className="datetime-picker">
                    <input type="date" value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)} className="date-input" />
                    <input type="time" value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      disabled={!scheduleDate}
                      className={`time-input${!scheduleDate ? " input-disabled" : ""}`} />
                    {scheduleDate && (
                      <button type="button" className="clear-date-btn"
                        onClick={() => { setScheduleDate(""); setScheduleTime("00:00"); }}>
                        <FiX size={12} />
                      </button>
                    )}
                  </div>
                  {scheduleDate && (
                    <span className="form-hint" style={{ textAlign: "center" }}>
                      Activates: {formatDateTimeWithTz(localInputToUTC(scheduleDate, scheduleTime))}
                    </span>
                  )}
                  {!scheduleDate && (
                    <span className="form-hint" style={{ textAlign: "center" }}>Link is active immediately if not scheduled</span>
                  )}
                </div>

                <div className={`form-group${redirectTime === 0 ? " timing-slider-disabled" : ""}`}>
                  <label>Preview page timing</label>
                  <div className="timing-slider-group">
                    <div className="timing-slider-track">
                      <input type="range" min={5} max={30} step={5}
                        value={redirectTime === 0 ? 10 : redirectTime}
                        onChange={(e) => setRedirectTime(Number(e.target.value))}
                        disabled={redirectTime === 0} />
                      <div className="timing-slider-ticks">
                        {[5, 10, 15, 20, 25, 30].map((v) => (
                          <span key={v} className={`timing-tick${(redirectTime === 0 ? 10 : redirectTime) === v ? " active" : ""}`}>
                            <span className="timing-tick-line" />
                            <span className="timing-tick-label">{v}s</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className="form-hint">How long the preview page is shown before redirecting. Disabled when instant redirect is on.</span>
                </div>

                <div className="form-group">
                  <label>Redirect type</label>
                  <select value={redirectType} onChange={(e) => setRedirectType(e.target.value)} className="form-select">
                    <option value="302">302 — Temporary redirect (default)</option>
                    <option value="301">301 — Permanent redirect</option>
                  </select>
                  <span className="form-hint">301 is cached by browsers. Use 302 unless you need permanent redirects for SEO.</span>
                </div>

                <div className="form-group">
                  <label>Password protection</label>
                  {link.has_password && !changePassword ? (
                    <div className="edit-password-status">
                      <span className="form-hint">This link is password protected.</span>
                      <div className="edit-password-actions">
                        <button type="button" className="btn-sm" onClick={() => setChangePassword(true)}>
                          Change password
                        </button>
                        <button type="button" className="btn-sm btn-sm-danger" onClick={() => { setChangePassword(true); setPassword(""); }}>
                          Remove password
                        </button>
                      </div>
                    </div>
                  ) : !link.has_password && !changePassword ? (
                    <div className="edit-password-status">
                      <span className="form-hint">No password set.</span>
                      <button type="button" className="btn-sm" onClick={() => setChangePassword(true)}>
                        Add password
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={link.has_password ? "Enter New Password (Keep Empty to Remove)" : "Enter password"}
                      />
                      <button type="button" className="btn-link-sm" onClick={() => { setChangePassword(false); setPassword(""); }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Expiration Fallback — collapsed by default, only relevant if expiry is set */}
            <div className="form-section-toggle" onClick={() => setShowFallback(!showFallback)}>
              <span>Expiration Fallback</span>
              {fallbackMode && <span className="form-section-badge">Configured</span>}
              {showFallback ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </div>
            {showFallback && (
              <div className="fallback-options">
                <p className="form-hint" style={{ marginBottom: 16 }}>
                  Configure what happens when visitors click an expired link.
                  {!expiryDate && <strong> Set an expiration date above to enable.</strong>}
                </p>

                <div className="form-group">
                  <label>When this link expires:</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="fallbackMode"
                        value=""
                        checked={fallbackMode === ""}
                        onChange={() => setFallbackMode("")}
                      />
                      <span>Show default expired page</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="fallbackMode"
                        value="redirect"
                        checked={fallbackMode === "redirect"}
                        onChange={() => setFallbackMode("redirect")}
                      />
                      <span>Redirect to another URL</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="fallbackMode"
                        value="page"
                        checked={fallbackMode === "page"}
                        onChange={() => setFallbackMode("page")}
                      />
                      <span>Show custom expired page</span>
                    </label>
                  </div>
                </div>

                {fallbackMode === "redirect" && (
                  <div className="form-group">
                    <label htmlFor="fallback-url">Fallback URL</label>
                    <input
                      id="fallback-url"
                      type="url"
                      value={fallbackUrl}
                      onChange={(e) => setFallbackUrl(e.target.value)}
                      placeholder="https://example.com/new-offer"
                      className={!fallbackUrlValid ? "input-error" : ""}
                    />
                    {!fallbackUrlValid && (
                      <span className="form-error">Please enter a valid URL (http:// or https://)</span>
                    )}
                    <span className="form-hint">Visitors will be immediately redirected here when the link expires</span>
                  </div>
                )}

                {fallbackMode === "page" && (
                  <>
                    <div className="form-group">
                      <label htmlFor="fallback-message">Custom Message</label>
                      <textarea
                        id="fallback-message"
                        value={fallbackMessage}
                        onChange={(e) => setFallbackMessage(e.target.value)}
                        placeholder="This offer has ended. Check out our latest deals!"
                        rows={3}
                        maxLength={500}
                      />
                      <span className="form-hint">This message will be shown to visitors</span>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="fallback-cta-text">Button Text</label>
                        <input
                          id="fallback-cta-text"
                          type="text"
                          value={fallbackCtaText}
                          onChange={(e) => setFallbackCtaText(e.target.value)}
                          placeholder="See Latest Deals"
                          maxLength={100}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="fallback-cta-url">Button URL</label>
                        <input
                          id="fallback-cta-url"
                          type="url"
                          value={fallbackCtaUrl}
                          onChange={(e) => setFallbackCtaUrl(e.target.value)}
                          placeholder="https://example.com/deals"
                          className={fallbackCtaUrl && !fallbackCtaUrlValid ? "input-error" : ""}
                        />
                        {fallbackCtaUrl && !fallbackCtaUrlValid && (
                          <span className="form-error">Please enter a valid URL (http:// or https://)</span>
                        )}
                      </div>
                    </div>

                    <div className="form-group checkbox-inline">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={fallbackAutoRedirect}
                          onChange={(e) => setFallbackAutoRedirect(e.target.checked)}
                          disabled={!fallbackCtaUrl}
                        />
                        <span>Auto-redirect after</span>
                      </label>
                      <input
                        type="number"
                        value={fallbackDelay}
                        onChange={(e) => setFallbackDelay(Math.max(1, Math.min(60, parseInt(e.target.value) || 5)))}
                        min={1}
                        max={60}
                        disabled={!fallbackAutoRedirect || !fallbackCtaUrl}
                        className={`delay-input${!fallbackDelayValid ? " input-error" : ""}`}
                      />
                      <span className="delay-label">seconds</span>
                    </div>
                    {!fallbackDelayValid && (
                      <span className="form-error">Delay must be between 1 and 60 seconds</span>
                    )}
                    {!fallbackCtaUrl && fallbackAutoRedirect && (
                      <span className="form-hint" style={{ color: "var(--warning)" }}>Add a Button URL to enable auto-redirect</span>
                    )}
                    {fallbackAutoRedirect && fallbackCtaUrl && (
                      <div className="countdown-preview">
                        <span className="countdown-preview-text">
                          Preview: Redirecting in <strong>{fallbackDelay}</strong>s → {fallbackCtaUrl.length > 40 ? fallbackCtaUrl.slice(0, 40) + "..." : fallbackCtaUrl}
                        </span>
                      </div>
                    )}

                    {/* Custom Branding */}
                    <div className="form-section-toggle" style={{ marginTop: 20, marginBottom: 12 }} onClick={() => {}}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>Custom Branding (optional)</span>
                      <span className="toggle-help">
                        <span className="toggle-help-icon">?</span>
                        <span className="toggle-tooltip">Replace the LinkSquire logo with your own brand on the expired page.</span>
                      </span>
                    </div>
                    <div className="form-group">
                      <label htmlFor="edit-custom-brand-icon">Brand Icon URL</label>
                      <input
                        id="edit-custom-brand-icon"
                        type="url"
                        value={customBrandIcon}
                        onChange={(e) => setCustomBrandIcon(e.target.value)}
                        placeholder="https://example.com/logo.png"
                      />
                      <span className="form-hint">Your logo will replace the LinkSquire icon on the expired page</span>
                    </div>
                    <div className="form-group">
                      <label htmlFor="edit-custom-brand-name">Brand Name</label>
                      <input
                        id="edit-custom-brand-name"
                        type="text"
                        value={customBrandName}
                        onChange={(e) => setCustomBrandName(e.target.value)}
                        placeholder="Your Brand Name"
                        maxLength={100}
                      />
                      <span className="form-hint">Text shown next to your brand icon (or alone if no icon)</span>
                    </div>
                    {(customBrandIcon || customBrandName) && (
                      <div className="form-group">
                        <label style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>Preview</label>
                        <div className="brand-preview-card">
                          {customBrandIcon && <img src={customBrandIcon} alt="Brand" className="brand-preview-icon" onError={(e) => e.target.style.display = 'none'} />}
                          {customBrandName && <span className="brand-preview-name">{customBrandName}</span>}
                          {!customBrandIcon && !customBrandName && <span className="brand-preview-name">LinkSquire</span>}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {fallbackMode && (
                  <button
                    type="button"
                    className="btn-clear-fallback"
                    onClick={() => {
                      setFallbackMode("");
                      setFallbackUrl("");
                      setFallbackMessage("");
                      setFallbackCtaText("");
                      setFallbackCtaUrl("");
                      setFallbackAutoRedirect(false);
                      setFallbackDelay(5);
                      setCustomBrandIcon("");
                      setCustomBrandName("");
                    }}
                  >
                    Clear fallback settings
                  </button>
                )}
              </div>
            )}

            {/* Info */}
            <div className="edit-form-section edit-info-section">
              <div className="edit-info-row">
                <span className="edit-info-label">Created</span>
                <span className="edit-info-value">{new Date(link.created_at).toLocaleString()}</span>
              </div>
              <div className="edit-info-row">
                <span className="edit-info-label">Total Clicks</span>
                <span className="edit-info-value">{link.click_count}</span>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Fixed footer */}
      <div className="edit-footer">
        <button
          type="button"
          className={`edit-footer-reset${hasChanges() ? "" : " disabled"}`}
          onClick={handleReset}
          disabled={!hasChanges()}
        >
          <FiRotateCcw size={14} />
          Reset
        </button>
        <div className="edit-footer-right">
          <button type="button" className="edit-footer-cancel" onClick={handleBack}>Cancel</button>
          <button type="submit" form="edit-form" className="edit-footer-save" disabled={loading || !fallbackFieldsValid}>
            {loading ? <><span className="spinner-sm" /> Saving...</> : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Unsaved changes confirmation */}
      {showConfirm && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h2>Unsaved changes</h2>
              <button className="modal-close" onClick={() => setShowConfirm(false)}><FiX /></button>
            </div>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
              You have unsaved changes. Are you sure you want to leave? All changes will be lost.
            </p>
            <div className="confirm-actions">
              <button className="btn-confirm-cancel" onClick={() => setShowConfirm(false)}>
                Keep editing
              </button>
              <button className="btn-confirm-discard" onClick={onClose}>
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle conflict confirmation */}
      {showToggleWarning && (
        <div className="modal-overlay" onClick={() => setShowToggleWarning(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Action Required</h2>
              <button className="modal-close" onClick={() => setShowToggleWarning(null)}><FiX /></button>
            </div>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
              {showToggleWarning === 'instant' 
                ? "Turning on instant redirect will disable Custom Social Previews and clear any unsaved custom preview data. Do you want to proceed?"
                : "Turning on Custom Social Previews will disable Instant Redirect, as custom previews require a minimum 5s preview page to prevent abuse. Do you want to proceed?"}
            </p>
            <div className="confirm-actions">
              <button className="btn-confirm-cancel" onClick={() => setShowToggleWarning(null)}>
                Cancel
              </button>
              <button className="btn-confirm-discard" onClick={confirmToggle} style={{ background: "var(--primary)", color: "#fff", borderColor: "var(--primary)" }}>
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
